<template>
  <HorizontalItemsScroller
    ref="slider"
    class="sibling-slider"
    :start-at-index="sliderIndex"
  >
    <ScrollerItem
      v-for="singleProduct in allProducts"
      :key="singleProduct.id"
      class="sibling-slider-item sm:aspect-w-24 group shrink-0 snap-start px-2 sm:aspect-h-8"
    >
      <NuxtLink :to="localePath(getProductDetailRoute(singleProduct))">
        <div>
          <ProductImage
            :key="singleProduct.id + '-image'"
            :image="getFirstProductImage(singleProduct)"
            :alt="`variant ${
              singleProduct.id
            } / ${productName} / ${getProductColor(singleProduct.attributes)}`"
            sizes="sm:20vw md:20vw"
            fit="contain"
            class="rounded border p-1 hover:border-gray-600"
            :class="{
              'border-black': singleProduct.id === currentProductId,
              'border-[#D7DDE3]': singleProduct.id !== currentProductId,
              'opacity-30': singleProduct.isSoldOut && !ignoreSoldOut,
            }"
          />
          <div
            v-if="getProductColor(singleProduct.attributes)"
            class="mt-2 truncate whitespace-normal pb-1 text-center text-base leading-5 group-hover:visible"
            :class="[
              singleProduct.id === currentProductId ? 'visible:' : 'invisible',
            ]"
          >
            {{ getProductColor(singleProduct.attributes) }}
          </div>
        </div>
      </NuxtLink>
    </ScrollerItem>
  </HorizontalItemsScroller>
</template>

<script setup lang="ts">
import { type Product, getFirstAttributeValue } from '@scayle/storefront-nuxt'
const localePath = useFimLocalePath()
const { getProductDetailRoute } = useRouteHelpers()

const props = defineProps({
  product: {
    type: Object as PropType<Product>,
    required: true,
  },
  ignoreSoldOut: {
    type: Boolean,
    default: false,
  },
})

const productName = computed(
  () =>
    getFirstAttributeValue(props.product.attributes, 'name')?.label ??
    'product',
)

const getProductColor = (attributes: any) => {
  return (
    getFirstAttributeValue(attributes, 'frameColor')?.label ||
    getFirstAttributeValue(attributes, 'colorDetail')?.label ||
    getFirstAttributeValue(attributes, 'baseColor')?.label ||
    ''
  )
}

const allProducts = computed(() => {
  if (!props.product.siblings) {
    return [props.product]
  }
  return [props.product, ...props.product.siblings].sort(
    (productA, productB) => productA.id - productB.id,
  )
})

const currentProductId = computed(() => props.product.id)

const sliderIndex = computed(() =>
  allProducts.value.findIndex((product: Product) => {
    return product.id === currentProductId.value
  }),
)
</script>

<style lang="scss" scoped>
/* stylelint-disable rule-empty-line-before */
.sibling-slider :deep(.slider-btn-prev) {
  padding: 0.5rem 1rem 0.5rem 0;
  left: -0.25rem;
  background: linear-gradient(
    90deg,
    rgb(255 255 255) 7.5%,
    rgba(255 255 255 / 0) 100%
  );
}
.sibling-slider :deep(.slider-btn-next) {
  padding: 0.5rem 0 0.5rem 1rem;
  right: -0.25rem;
  background: linear-gradient(
    270deg,
    rgb(255 255 255) 0%,
    rgba(255 255 255 / 0) 100%
  );
}

.sibling-slider :deep(.slider-btn-prev),
.sibling-slider :deep(.slider-btn-next) {
  border-radius: 0;
  box-sizing: border-box;
  position: absolute;
  top: 0;
  height: 48px;
  z-index: 20;
  transform: none;
  svg {
    path {
      fill: #000000;
    }
  }
}

.sibling-slider :deep(.wrapper) {
  &::before {
    display: none;
  }
  &::after {
    display: none;
  }
}
.sibling-slider :deep(.scroller) {
  min-width: calc(100% + 1rem);
  margin-left: -0.5rem;
}
</style>

<style>
/* stylelint-disable */
.sibling-slider {
  container-type: inline-size;
}

.sibling-slider-item {
  width: 25%;
}

@container (min-width: 460px) {
  .sibling-slider-item {
    width: 20%;
  }
}

@container (min-width: 600px) {
  .sibling-slider-item {
    width: 12.5%;
  }
}
/* stylelint-enable */
</style>
